import { useTranslation } from "react-i18next";
import React, { useEffect, useMemo, useState } from "react";
import { Bike, Search, Phone, Mail, MapPin, FileText, ShieldCheck, ShieldOff, UserRound, ChevronDown, ChevronUp, Power, Loader2 } from "lucide-react";
import { apiClient, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const VEHICLE_LABELS = { moto: "Moto", scooter: "Scooter", velo: "Vélo", a_pied: "À pied" };
const ID_LABELS = { cni: "CNI guinéenne", passeport: "Passeport", carte_sejour: "Carte de séjour" };

/** Onglet admin « Livreurs » : tous les livreurs avec informations complètes
 * et accès aux documents fournis à la candidature. */
export const DriversPanel = () => {
  const { t } = useTranslation();
  const [drivers, setDrivers] = useState(null);
  const [q, setQ] = useState("");
  const [openId, setOpenId] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = () => apiClient.get("/admin/drivers")
    .then(({ data }) => setDrivers(data))
    .catch((err) => toast.error(formatApiError(err)));

  useEffect(() => { load(); }, []);

  const suspend = async (d) => {
    const reason = prompt(`Motif de suspension de ${d.name} ?\n(ex : sans permis, incidents répétés, comportement signalé…)`);
    if (reason === null) return;
    if (!reason.trim()) { toast.error(t("admin.reasonRequired")); return; }
    setBusyId(d.user_id);
    try {
      await apiClient.post(`/admin/drivers/${d.user_id}/suspend`, { reason: reason.trim() });
      toast.success(`${d.name} suspendu — hors ligne et connexion bloquée`);
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusyId(null);
    }
  };

  const unsuspend = async (d) => {
    if (!confirm(`Réactiver ${d.name} ? Il pourra de nouveau se connecter et passer En ligne.`)) return;
    setBusyId(d.user_id);
    try {
      await apiClient.post(`/admin/drivers/${d.user_id}/unsuspend`);
      toast.success(`${d.name} réactivé`);
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusyId(null);
    }
  };

  const filtered = useMemo(() => {
    if (!drivers) return [];
    const s = q.trim().toLowerCase();
    const sDigits = s.replace(/[^\d]/g, "");
    return drivers.filter((d) => {
      if (!s) return true;
      return (d.name || "").toLowerCase().includes(s)
        || (d.email || "").toLowerCase().includes(s)
        || (sDigits && (d.phone || "").replace(/[^\d]/g, "").includes(sDigits));
    });
  }, [drivers, q]);

  if (!drivers) return <div className="text-mute text-sm py-10 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></div>;

  return (
    <div className="space-y-3" data-testid="admin-drivers-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="relative max-w-md flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-mute" />
          <input value={q} onChange={(e) => setQ(e.target.value)}
            placeholder={t("admin.searchDriver")}
            data-testid="drivers-search"
            className="w-full bg-white border border-sand rounded-full pl-10 pr-4 py-2.5 text-sm outline-none focus:border-brand-red" />
        </div>
        <div className="text-sm text-mute">{t("admin.driversCount", { count: filtered.length })}</div>
      </div>

      {filtered.length === 0 && (
        <div className="bg-white border border-sand rounded-2xl p-8 text-center text-mute">{t("admin.noDriverMatch")}</div>
      )}

      {filtered.map((d) => {
        const open = openId === d.user_id;
        return (
          <div key={d.user_id} className="bg-white border border-sand rounded-2xl overflow-hidden" data-testid={`driver-card-${d.user_id}`}>
            {/* En-tête */}
            <button onClick={() => setOpenId(open ? null : d.user_id)}
              data-testid={`driver-card-toggle-${d.user_id}`}
              className="w-full flex items-center justify-between gap-3 p-4 text-left hover:bg-elevated/50">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 rounded-full bg-[#FFF5F6] text-brand-red grid place-items-center shrink-0"><Bike className="w-5 h-5" /></div>
                <div className="min-w-0">
                  <div className="font-display font-semibold flex items-center gap-2 flex-wrap">
                    {d.name}
                    <span className="font-mono text-[11px] text-mute font-normal">#{d.user_id.slice(-4).toUpperCase()}</span>
                    {d.available
                      ? <Pill bg="#E7F6EE" fg="#009E60">En ligne</Pill>
                      : <Pill bg="#EEE" fg="#666">{t("admin.offline")}</Pill>}
                    {d.suspended && <Pill bg="#E32636" fg="#fff">Suspendu</Pill>}
                    {d.quiz_passed === false && <Pill bg="#FFF7DB" fg="#8A6D00">Quiz en attente — {d.quiz_attempts || 0} tentative{(d.quiz_attempts || 0) > 1 ? "s" : ""}</Pill>}
                    {d.quiz_passed === true && <Pill bg="#E7F6EE" fg="#009E60">Quiz réussi{d.quiz_passed_at ? ` le ${new Date(d.quiz_passed_at).toLocaleDateString("fr-FR")}` : ""}{d.quiz_passed_attempt ? `, tentative n°${d.quiz_passed_attempt}` : ""}</Pill>}
                    {d.has_license === false && <Pill bg="#E32636" fg="#fff">Sans permis</Pill>}
                    {d.is_vehicle_owner === false && <Pill bg="#FFF7DB" fg="#8A6D00">{t("admin.notOwner")}</Pill>}
                  </div>
                  <div className="text-xs text-mute mt-0.5 flex items-center gap-3 flex-wrap">
                    {d.phone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" />{d.phone}</span>}
                    {d.email && <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" />{d.email}</span>}
                    {d.zone && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{d.zone}</span>}
                    {d.vehicle && <span>{VEHICLE_LABELS[d.vehicle] || d.vehicle}</span>}
                  </div>
                </div>
              </div>
              {open ? <ChevronUp className="w-4 h-4 text-mute shrink-0" /> : <ChevronDown className="w-4 h-4 text-mute shrink-0" />}
            </button>

            {/* Détails */}
            {open && (
              <div className="border-t border-sand p-4 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid={`driver-details-${d.user_id}`}>
                {d.suspended && (
                  <div className="md:col-span-2 rounded-xl bg-[#FDECEE] border border-brand-red/20 px-4 py-3 text-sm" data-testid={`driver-suspended-info-${d.user_id}`}>
                    <b className="text-brand-red">Compte suspendu</b>
                    {d.suspended_at ? ` le ${new Date(d.suspended_at).toLocaleDateString("fr-FR")}` : ""} — motif : <b>{d.suspended_reason || "non précisé"}</b>. Connexion bloquée et hors ligne forcé.
                  </div>
                )}
                <InfoBlock title={t("admin.identity")} icon={<UserRound className="w-3.5 h-3.5" />}>
                  <Row k={t("admin.fullName")} v={d.name} />
                  <Row k={t("admin.birthDate")} v={d.birth_date ? `${d.birth_date}${d.age_at_application ? ` (${d.age_at_application} ans à la candidature)` : ""}` : "—"} />
                  <Row k={t("admin.idDoc")} v={ID_LABELS[d.id_doc_type] || "—"} />
                  <Row k={t("admin.registeredOn")} v={d.created_at ? new Date(d.created_at).toLocaleDateString("fr-FR") : "—"} />
                </InfoBlock>
                <InfoBlock title={t("admin.vehicleGuarantor")} icon={<Bike className="w-3.5 h-3.5" />}>
                  <Row k={t("admin.vehicle")} v={VEHICLE_LABELS[d.vehicle] || d.vehicle || "—"} />
                  <Row k={t("admin.owner")} v={d.is_vehicle_owner === false ? `Non — ${d.vehicle_owner_name || "?"}` : d.is_vehicle_owner === true ? "Oui" : "—"} />
                  <Row k={t("admin.license")} v={d.has_license === false ? "❌ Sans permis" : d.has_license === true ? "Oui" : "—"} />
                  <Row k={t("admin.guarantor")} v={d.guarantor_name ? `${d.guarantor_name}${d.guarantor_phone ? ` · ${d.guarantor_phone}` : ""}` : "Non renseigné"} />
                  {d.preferred_zones?.length > 0 && <Row k={t("admin.prefZones")} v={d.preferred_zones.join(", ")} />}
                </InfoBlock>
                <div className="md:col-span-2">
                  <div className="text-[10px] uppercase tracking-widest text-mute font-semibold mb-2 flex items-center gap-1.5">
                    <FileText className="w-3.5 h-3.5" /> Documents fournis
                  </div>                  {(d.id_doc_front_url || d.vehicle_registration_url || d.vehicle_authorization_url || d.license_front_url || d.insurance_url) ? (
                    <div className="flex flex-wrap gap-1.5" data-testid={`driver-docs-${d.user_id}`}>
                      <Doc url={d.id_doc_front_url} label={`${ID_LABELS[d.id_doc_type]?.split(" ")[0] || "Pièce"} recto`} />
                      <Doc url={d.id_doc_back_url} label={t("admin.docBack")} />
                      <Doc url={d.vehicle_registration_url} label={t("admin.docRegistration")} />
                      <Doc url={d.vehicle_authorization_url} label={t("admin.docAuthorization")} />
                      <Doc url={d.license_front_url} label={t("admin.docLicenseFront")} />
                      <Doc url={d.license_back_url} label={t("admin.docLicenseBack")} />
                      <Doc url={d.insurance_url} label={t("admin.docInsurance")} />
                    </div>
                  ) : (
                    <div className="text-sm text-mute">{t("admin.noDocs")}</div>
                  )}
                </div>
                {/* Actions */}
                <div className="md:col-span-2 flex justify-end">
                  {d.suspended ? (
                    <button onClick={() => unsuspend(d)} disabled={busyId === d.user_id}
                      data-testid={`driver-unsuspend-${d.user_id}`}
                      className="inline-flex items-center gap-1.5 rounded-full bg-brand-green text-white px-4 py-2 text-sm font-semibold disabled:opacity-60">
                      {busyId === d.user_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
                      Réactiver ce livreur
                    </button>
                  ) : (
                    <button onClick={() => suspend(d)} disabled={busyId === d.user_id}
                      data-testid={`driver-suspend-${d.user_id}`}
                      title="Passe le livreur hors ligne et bloque sa connexion jusqu'à réactivation"
                      className="inline-flex items-center gap-1.5 rounded-full border border-brand-red text-brand-red hover:bg-brand-red hover:text-white px-4 py-2 text-sm font-semibold disabled:opacity-60">
                      {busyId === d.user_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldOff className="w-4 h-4" />}
                      Suspendre ce livreur
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

function Pill({ bg, fg, children }) {
  return <span className="text-[10px] uppercase tracking-wide font-bold rounded-full px-2 py-0.5" style={{ backgroundColor: bg, color: fg }}>{children}</span>;
}

function InfoBlock({ title, icon, children }) {
  return (
    <div className="bg-elevated rounded-xl p-3">
      <div className="text-[10px] uppercase tracking-widest text-mute font-semibold mb-2 flex items-center gap-1.5">{icon} {title}</div>
      <div className="space-y-1.5">{children}</div>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex justify-between gap-3 text-sm">
      <span className="text-mute">{k}</span>
      <span className="font-medium text-right">{v}</span>
    </div>
  );
}

function Doc({ url, label }) {
  if (!url) return null;
  return (
    <a href={url} target="_blank" rel="noreferrer"
      className="inline-flex items-center gap-1 rounded-full border border-sand bg-white hover:border-brand-red hover:text-brand-red px-3 py-1.5 text-xs font-semibold text-ink">
      <FileText className="w-3.5 h-3.5" /> {label}
    </a>
  );
}
